import { ReactElement, useEffect, useMemo, useState } from 'react';
import {
  Chip,
  Typography,
  BreadcrumbHeading,
  Prompt,
  Export,
  EditIcon,
  DownloadIcon,
  TimerIcon,
  TickIcon,
  DuplicateIcon,
  LinkIcon,
  ValidationIcon,
  Bin,
  ConfigureIcon,
  Spinner,
  PrimaryButton,
  SecondaryButton,
  useSnackbar,
  SnackbarStatus,
  useTray,
} from '@weareopenr/component-library';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthWrapper } from '@weareopenr/authkit';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import MenuSectionItems from 'src/components/MenuSectionItems';
import PublishModal from '../../components/PublishModal';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import { SpinnerIcon } from 'src/components/SpinnerIcon';
import { formatDate } from 'src/helpers/formatDate/formatDate';
import { formatTime } from 'src/helpers/formatTime/formatTime';
import { cancelScheduledPublish } from 'src/api/cancelScheduledPublish/cancelScheduledPublish';
import { CancelScheduledPublicationPrompt } from 'src/components/CancelScheduledPublicationPrompt';
import { error } from 'src/mocks/data';
import styles from './Menu.module.scss';
import { useMenu } from 'src/hooks/useMenu/useMenu';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { IMenu } from 'src/types/menu.types';
import { ValidationStatus } from 'src/api/getSchedules/types';
import { getPublicationStatus } from 'src/helpers/getPublicationStatus/getPublicationStatus';

import { Routes } from 'src/routes';
import ExportModal from 'src/components/ExportModal';
import MenuModal from 'src/components/MenuModal';
import { boldMenuName } from 'src/helpers/truncateMenuName/boldMenuName';
import { MenuInUsePrompt } from 'src/components/MenuInUsePrompt';
import { useDeleteMenuPrompt } from 'src/hooks/useDeleteMenuPrompt/useDeleteMenuPrompt';
import LinkVenuesModal from 'src/components/LinkVenuesModal';
import EditMenuRanges from 'src/components/EditMenuRangesModal';
import MenuValidationModal from 'src/components/MenuValidationModal/MenuValidationModal';
import { ValidationReportContainer } from 'src/components/ValidationReportContainer';
import { usePriceSchemes } from 'src/hooks/usePriceSchemes';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { FEATURE_FLAGS } from 'src/constants';

const Menu = (): ReactElement => {
  const [isPublishModalOpen, setIsPublishModalOpen] = useState<boolean>(false);
  const [isCancelPromptVisible, setIsCancelPromptVisible] = useState<boolean>(false);
  const [isTrial, setIsTrial] = useState<boolean>(false);
  const [viewMoreRanges, setViewMoreRanges] = useState<boolean>(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
  const [isMenuModalOpen, setIsMenuModalOpen] = useState<boolean>(false);
  const [isLinkVenuesModalOpen, setIsLinkVenuesModalOpen] = useState<boolean>(false);
  const [isConfigureRangesModalOpen, setIsConfigureRangesModalOpen] = useState<boolean>(false);
  const [isValidationModalOpen, setIsValidationModalOpen] = useState<boolean>(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState<boolean>(false);
  const [menuModalVariant, setMenuModalVariant] = useState<'update' | 'duplicate'>('update');
  const { menuId } = useParams<{ menuId: string }>();
  const { isTrayOpen } = useTray();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlReportId = searchParams.get('report');
  const { data: currentMenu, isLoading } = useMenu(menuId);
  const { data } = usePriceSchemes();
  const { trackEvent, captureException } = useUserTracking();
  const { featureFlags } = useAuthWrapper();
  const isEnhancedSchedulingEnabled = featureFlags.includes(FEATURE_FLAGS.ENHANCED_SCHEDULING);
  const scheduledPublicationsDate = currentMenu?.scheduledPublications?.[0]?.publicationDate;
  const validationStatus = currentMenu?.validation?.status;
  const isValidationInProgress = validationStatus === ValidationStatus.VALIDATING;
  const reportId = urlReportId ?? currentMenu?.validation?.report_id ?? null;
  const ranges = currentMenu?.ranges || [];
  const displayRanges = viewMoreRanges ? ranges : ranges.slice(0, 5);
  const priceSchemes = data?.map((scheme) => ({
    name: scheme.name,
    value: scheme.id,
  }));
  const publicationSubtitle = (menu?: IMenu) => {
    if (!menu) {
      return '';
    }

    const { publication, created, validation } = menu;
    if (!publication || !created) {
      return '';
    }

    const { publicationDate, publicationStatus } = publication;
    const creationDate = formatDate(created);
    const status = getPublicationStatus(publicationStatus, publicationDate);
    const lastValidated = validation?.validated_at
      ? `Last validated - ${formatDate(new Date(validation.validated_at))}, ${formatTime(
          new Date(validation.validated_at),
        )} • `
      : '';

    return `Created - ${creationDate} • ${lastValidated}${status}`;
  };

  const handlePublishFlow = () => {
    setIsTrial(false);
    setIsPublishModalOpen((prev: boolean) => !prev);
  };

  const handleTrialPublishFlow = () => {
    setIsTrial(true);
    setIsPublishModalOpen((prev: boolean) => !prev);
  };

  const handleCancelPrompt = () => {
    setIsCancelPromptVisible((prev: boolean) => !prev);
  };

  const handleExportModal = () => {
    setIsExportModalOpen((prev) => !prev);
  };

  const handleMenuModal = (value?: 'duplicate' | 'update') => {
    if (value) {
      setMenuModalVariant(value);
    }
    setIsMenuModalOpen((prev) => !prev);
  };

  const handleLinkVenuesModal = () => {
    setIsLinkVenuesModalOpen((prev) => !prev);
  };

  const handleConfigureRangesModal = () => {
    setIsConfigureRangesModalOpen((prev) => !prev);
  };

  const handleValidationModal = () => {
    setIsValidationModalOpen((prev) => !prev);
  };

  const handleValidationReportModal = () => {
    setIsReportModalOpen((prev) => !prev);
  };

  useEffect(() => {
    document.title = currentMenu?.name || 'Openr';
    if (currentMenu?.menuId) {
      trackEvent('page_viewed', { page: 'menu_detail', menuId: currentMenu.menuId });
    }
  }, [currentMenu?.menuId]);

  useEffect(() => {
    if (urlReportId) setIsReportModalOpen(true);
  }, [urlReportId]);

  const closeReportModal = () => {
    setIsReportModalOpen(false);
    if (urlReportId) {
      const params = new URLSearchParams(searchParams);
      params.delete('report');
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  };

  const cancelPublish = async () => {
    try {
      await cancelScheduledPublish(menuId);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });
      handleCancelPrompt();
      trackEvent('menu_publish_cancelled', { menuId, menuName: currentMenu?.name });
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: <>You have successfully cancelled the scheduled {boldMenuName(currentMenu?.name)} publication.</>,
      });
    } catch (e) {
      captureException(e, { tags: { action: 'cancel_publish', menuId } });
      showSnackbar({ status: SnackbarStatus.ERROR, message: error.message });
    }
  };

  const subtitle = publicationSubtitle(currentMenu);

  const {
    isRemovePromptVisible,
    isMenuInUsePromptVisible,
    menuInUseErrorMessage,
    isSubmitting,
    isCheckingCanDelete,
    openDeletePrompt,
    closeDeletePrompt,
    closeMenuInUsePrompt,
    confirmDelete,
  } = useDeleteMenuPrompt({
    menuId,
    genericErrorMessage: error.message,
    onDeleted: async () => {
      trackEvent('menu_deleted', { menuId, menuName: currentMenu?.name });
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: <>You have successfully deleted &quot;{boldMenuName(currentMenu?.name)}&quot;.</>,
      });
      navigate('/');
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menus] });
    },
    onCheckError: (err) => captureException(err, { tags: { action: 'delete_menu_check', menuId } }),
    onDeleteError: (err) => captureException(err, { tags: { action: 'delete_menu', menuId } }),
  });

  const optionMenuItems = [
    {
      id: 'publish-trial',
      name: 'Publish to trial venues',
      onOptionClick: () => handleTrialPublishFlow(),
      icon: Export,
    },
    {
      id: 'edit-menu',
      name: 'Edit',
      onOptionClick: () => handleMenuModal('update'),
      icon: EditIcon,
    },
    {
      id: 'export-menu',
      name: 'Export Options',
      onOptionClick: handleExportModal,
      icon: DownloadIcon,
    },
    {
      id: 'view-validation-report',
      name: 'View Validation Report',
      onOptionClick: handleValidationReportModal,
      isDisabled: !reportId || validationStatus !== ValidationStatus.VALIDATION_FAILED,
      icon: ValidationIcon,
    },
    { id: 'divider-1', isDivider: true as const },
    {
      id: 'configure-ranges',
      name: 'Configure Ranges',
      onOptionClick: handleConfigureRangesModal,
      icon: ConfigureIcon,
    },
    {
      id: 'mealtimes',
      name: 'Mealtimes',
      onOptionClick: () => navigate(`${location.pathname}/mealtimes`),
      icon: TimerIcon,
    },
    {
      id: 'publish-status',
      name: 'View publish status',
      onOptionClick: () => navigate(`${location.pathname}/status`),
      isDisabled: currentMenu?.publication?.publicationStatus === 'UNPUBLISHED',
      icon: TickIcon,
    },
    {
      id: 'link-venues',
      name: 'Link venues',
      onOptionClick: handleLinkVenuesModal,
      icon: LinkIcon,
    },
    { id: 'divider-utils', isDivider: true as const },
    {
      id: 'duplicate-menu',
      name: 'Duplicate',
      onOptionClick: () => handleMenuModal('duplicate'),
      icon: DuplicateIcon,
    },
    {
      id: 'delete-menu',
      name: 'Delete',
      onOptionClick: openDeletePrompt,
      isDisabled: isCheckingCanDelete,
      persistMenuOnClick: true,
      hoverColour: 'tomato' as const,
      icon: isCheckingCanDelete ? SpinnerIcon : Bin,
    },
  ];

  return (
    <>
      {isLoading ? (
        <SpinnerContainer data-testid="loading-spinner">
          <Spinner size="xlarge" colour="rainbow" />
        </SpinnerContainer>
      ) : (
        <>
          <BreadcrumbHeading
            breadcrumbs={[
              { label: 'Menus', path: Routes.MENUS },
              { label: currentMenu?.name, path: `${Routes.MENUS}/${currentMenu?.menuId}` },
            ]}
            primaryButton={
              isTrayOpen ? undefined : isEnhancedSchedulingEnabled ? (
                <PrimaryButton onClick={handlePublishFlow} icon={Export}>
                  Publish menu
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  onClick={scheduledPublicationsDate ? handleCancelPrompt : handlePublishFlow}
                  icon={scheduledPublicationsDate ? TimerIcon : Export}
                >
                  {scheduledPublicationsDate ? 'Scheduled' : 'Publish menu'}
                </PrimaryButton>
              )
            }
            secondaryButton={
              isTrayOpen ? undefined : (
                <SecondaryButton
                  onClick={handleValidationModal}
                  icon={ValidationIcon}
                  disabled={isValidationInProgress}
                >
                  Validate
                </SecondaryButton>
              )
            }
            subtitle={subtitle}
            optionMenuItems={isTrayOpen ? [] : optionMenuItems}
          />
          <div className={styles['ranges']}>
            {ranges.length > 0 && (
              <Typography size="xsmall" className={styles['label']}>
                Range(s) -
              </Typography>
            )}
            {displayRanges.map((range) => (
              <Chip label={range.name} key={range.range_id} backgroundColour="grape" />
            ))}
            {ranges.length > 5 && (
              <div onClick={() => setViewMoreRanges(!viewMoreRanges)}>
                <Typography size="xsmall" weight="bold" className={styles['view-button']}>
                  {`View ${viewMoreRanges ? 'less' : 'more'}`}
                </Typography>
              </div>
            )}
          </div>
          <MenuSectionItems menu={currentMenu} />
          <PublishModal
            isOpen={isPublishModalOpen}
            closeModal={handlePublishFlow}
            menuId={menuId}
            menuName={currentMenu.name}
            isTrial={isTrial}
          />
          <CancelScheduledPublicationPrompt
            name={currentMenu.name}
            scheduleDate={currentMenu.scheduledPublications?.[0]?.publicationDate}
            closePrompt={handleCancelPrompt}
            open={isCancelPromptVisible}
            deleteButtonHandler={cancelPublish}
          />
          <MenuModal
            image={currentMenu?.image}
            title={`${menuModalVariant === 'update' ? 'Edit' : 'Duplicate'} menu`}
            closeModal={() => handleMenuModal()}
            open={isMenuModalOpen}
            variant={menuModalVariant}
            id={menuId}
            menuName={currentMenu?.name}
            consumerName={currentMenu?.consumerName}
          />
          <ExportModal
            title="Export Options"
            closeModal={handleExportModal}
            open={isExportModalOpen}
            id={menuId}
            name={currentMenu?.name}
            priceSchemes={priceSchemes}
            allergensPDF={currentMenu?.allergensPDF}
            mealtimes={currentMenu?.mealtimes}
            sections={currentMenu?.sections}
            items={currentMenu?.uncategorised}
          />
          <EditMenuRanges
            closeModal={handleConfigureRangesModal}
            menuId={menuId}
            isOpen={isConfigureRangesModalOpen}
            currentRanges={currentMenu?.ranges}
          />

          <Prompt
            promptHeading={`Are you sure you want to delete "${currentMenu?.name}"?`}
            closePrompt={closeDeletePrompt}
            open={isRemovePromptVisible}
            buttonHandler={confirmDelete}
            buttonText="Delete"
            buttonColour="tomato"
            backButton={true}
            backButtonText="Cancel"
            size="small"
            isSubmitting={isSubmitting}
          >
            <Typography className={styles['delete-prompt-content']} size="medium">
              Deleting this menu will <u>not</u> delete any product.
            </Typography>
            <Typography size="small">This action cannot be undone.</Typography>
          </Prompt>
          <MenuInUsePrompt
            open={isMenuInUsePromptVisible}
            closePrompt={closeMenuInUsePrompt}
            message={menuInUseErrorMessage}
            menuName={currentMenu?.name}
          />
          <LinkVenuesModal
            closeModal={handleLinkVenuesModal}
            open={isLinkVenuesModalOpen}
            menuId={menuId}
            menu={currentMenu}
            status="success"
          />
          <MenuValidationModal
            isOpen={isValidationModalOpen}
            closeModal={handleValidationModal}
            menuId={menuId}
            menuName={currentMenu.name}
          />
          {reportId && (
            <ValidationReportContainer isOpen={isReportModalOpen} closeModal={closeReportModal} reportId={reportId} />
          )}
        </>
      )}
    </>
  );
};

export default Menu;
